import type { Dict } from '../../i18n';

const dict: Dict = {
  en: {
    // ── Topbar ────────────────────────────────────────────────────────────
    topbar_sub: 'permissions · offices',
    heading: 'Settings',

    // ── Vault banner ──────────────────────────────────────────────────────
    vault_title: 'Integrations are configured in the deploy vault',
    /** "To connect a provider, edit the encrypted vault on the server" */
    vault_body_pre: 'To connect a provider, edit the encrypted vault on the server',
    /** "set the relevant" (before the *_MODE=live mono span) */
    vault_body_mid: 'set the relevant',
    /** "and provider keys, then redeploy. This screen is" (before the <strong>) */
    vault_body_post_pre: 'and provider keys, then redeploy. This screen is',
    vault_readonly: 'read-only',
    /** "in Phase 1 — it reflects the currently deployed configuration and does not change it." */
    vault_body_post: 'in Phase 1 — it reflects the currently deployed configuration and does not change it.',

    // ── Integration table ─────────────────────────────────────────────────
    integrations_heading: 'Integrations',
    btn_refresh: 'Refresh',
    loading_integrations: 'Loading integration status…',
    empty_integrations_title: 'No integrations',
    empty_integrations_hint: 'Nothing configured.',
    col_integration: 'Integration',
    col_status: 'Status',
    col_mode: 'Mode',
    col_notes: 'Notes',

    // ── Status dot labels ─────────────────────────────────────────────────
    status_connected: 'connected',
    status_live: 'live (sandbox)',
    status_stub: 'stub',
    status_unknown: 'not configured',

    // ── Offices management ────────────────────────────────────────────────
    offices_heading: 'Offices',
    offices_hint: 'Inactive offices stay in history but are hidden from pickers.',
    offices_delete_hint:
      'Existing orders and agents keep the office — deleting only removes it from the list.',
    offices_add_placeholder: 'New office',
    offices_loading: 'Loading offices…',
    offices_empty_title: 'No offices',
    offices_empty_hint: 'Add your first office above.',
    offices_col_name: 'Name',
    offices_col_code: 'Code',
    offices_col_status: 'Status',
    offices_status_active: 'Active',
    offices_status_inactive: 'Inactive',
    btn_add: 'Add',
    btn_save: 'Save',
    btn_cancel: 'Cancel',
    btn_delete: 'Delete',
    btn_edit: 'Edit',
    btn_activate: 'Activate',
    btn_deactivate: 'Deactivate',
    offices_delete_confirm: 'Delete this office? Existing records keep it.',

    // ── System panel ──────────────────────────────────────────────────────
    system_heading: 'System',
    label_domain: 'Domain',
    label_region: 'Region',
    region_value: 'OVH Public Cloud · 3 AZ + provider backups',
    label_fiscal: 'Fiscal system of record',
    fiscal_value: 'Holded (Veri*factu / SII)',
    label_retention: 'Fiscal retention',
    retention_value: '4–6 years (takes precedence over erasure)',
    label_erasure: 'Right to erasure',
    erasure_value: 'Anonymise non-fiscal PII',
    label_backups: 'Backups',
    backups_value: 'Nightly pg_dump on top of provider backups',
  },

  es: {
    // ── Topbar ────────────────────────────────────────────────────────────
    topbar_sub: 'permisos · oficinas',
    heading: 'Ajustes',

    // ── Vault banner ──────────────────────────────────────────────────────
    vault_title: 'Las integraciones se configuran en el vault de despliegue',
    vault_body_pre: 'Para conectar un proveedor, edita el vault cifrado en el servidor',
    vault_body_mid: 'establece el',
    vault_body_post_pre: 'correspondiente y las claves del proveedor, y redespliegua. Esta pantalla es',
    vault_readonly: 'solo lectura',
    vault_body_post: 'en la Fase 1 — refleja la configuración desplegada actualmente y no la modifica.',

    // ── Integration table ─────────────────────────────────────────────────
    integrations_heading: 'Integraciones',
    btn_refresh: 'Actualizar',
    loading_integrations: 'Cargando estado de integraciones…',
    empty_integrations_title: 'Sin integraciones',
    empty_integrations_hint: 'Nada configurado.',
    col_integration: 'Integración',
    col_status: 'Estado',
    col_mode: 'Modo',
    col_notes: 'Notas',

    // ── Status dot labels ─────────────────────────────────────────────────
    status_connected: 'conectado',
    status_live: 'live (sandbox)',
    status_stub: 'stub',
    status_unknown: 'sin configurar',

    // ── Offices management ────────────────────────────────────────────────
    offices_heading: 'Oficinas',
    offices_hint: 'Las oficinas inactivas siguen en el historial pero no aparecen en los selectores.',
    offices_delete_hint:
      'Los pedidos y agentes existentes conservan la oficina — eliminar solo la quita de la lista.',
    offices_add_placeholder: 'Nueva oficina',
    offices_loading: 'Cargando oficinas…',
    offices_empty_title: 'Sin oficinas',
    offices_empty_hint: 'Añade tu primera oficina arriba.',
    offices_col_name: 'Nombre',
    offices_col_code: 'Código',
    offices_col_status: 'Estado',
    offices_status_active: 'Activa',
    offices_status_inactive: 'Inactiva',
    btn_add: 'Añadir',
    btn_save: 'Guardar',
    btn_cancel: 'Cancelar',
    btn_delete: 'Eliminar',
    btn_edit: 'Editar',
    btn_activate: 'Activar',
    btn_deactivate: 'Desactivar',
    offices_delete_confirm: '¿Eliminar esta oficina? Los registros existentes la conservan.',

    // ── System panel ──────────────────────────────────────────────────────
    system_heading: 'Sistema',
    label_domain: 'Dominio',
    label_region: 'Región',
    region_value: 'OVH Public Cloud · 3 AZ + copias de seguridad del proveedor',
    label_fiscal: 'Sistema fiscal de referencia',
    fiscal_value: 'Holded (Veri*factu / SII)',
    label_retention: 'Retención fiscal',
    retention_value: '4–6 años (tiene prioridad sobre la supresión)',
    label_erasure: 'Derecho de supresión',
    erasure_value: 'Anonimizar los datos personales no fiscales',
    label_backups: 'Copias de seguridad',
    backups_value: 'pg_dump nocturno además de las copias del proveedor',
  },

  de: {
    // ── Topbar ────────────────────────────────────────────────────────────
    topbar_sub: 'Berechtigungen · Niederlassungen',
    heading: 'Einstellungen',

    // ── Vault banner ──────────────────────────────────────────────────────
    vault_title: 'Integrationen werden im Deploy-Tresor konfiguriert',
    vault_body_pre: 'Um einen Anbieter zu verbinden, bearbeiten Sie den verschlüsselten Tresor auf dem Server',
    vault_body_mid: 'setzen Sie das entsprechende',
    vault_body_post_pre: 'und die Anbieterschlüssel, und stellen Sie erneut bereit. Dieser Bildschirm ist',
    vault_readonly: 'schreibgeschützt',
    vault_body_post: 'in Phase 1 — er spiegelt die aktuell bereitgestellte Konfiguration wider und ändert sie nicht.',

    // ── Integration table ─────────────────────────────────────────────────
    integrations_heading: 'Integrationen',
    btn_refresh: 'Aktualisieren',
    loading_integrations: 'Integrationsstatus wird geladen…',
    empty_integrations_title: 'Keine Integrationen',
    empty_integrations_hint: 'Nichts konfiguriert.',
    col_integration: 'Integration',
    col_status: 'Status',
    col_mode: 'Modus',
    col_notes: 'Notizen',

    // ── Status dot labels ─────────────────────────────────────────────────
    status_connected: 'verbunden',
    status_live: 'live (Sandbox)',
    status_stub: 'Stub',
    status_unknown: 'nicht konfiguriert',

    // ── Offices management ────────────────────────────────────────────────
    offices_heading: 'Niederlassungen',
    offices_hint: 'Inaktive Niederlassungen bleiben in der Historie, werden aber in Auswahllisten ausgeblendet.',
    offices_delete_hint:
      'Bestehende Bestellungen und Agenten behalten die Niederlassung — Löschen entfernt sie nur aus der Liste.',
    offices_add_placeholder: 'Neue Niederlassung',
    offices_loading: 'Niederlassungen werden geladen…',
    offices_empty_title: 'Keine Niederlassungen',
    offices_empty_hint: 'Fügen Sie oben Ihre erste Niederlassung hinzu.',
    offices_col_name: 'Name',
    offices_col_code: 'Code',
    offices_col_status: 'Status',
    offices_status_active: 'Aktiv',
    offices_status_inactive: 'Inaktiv',
    btn_add: 'Hinzufügen',
    btn_save: 'Speichern',
    btn_cancel: 'Abbrechen',
    btn_delete: 'Löschen',
    btn_edit: 'Bearbeiten',
    btn_activate: 'Aktivieren',
    btn_deactivate: 'Deaktivieren',
    offices_delete_confirm: 'Diese Niederlassung löschen? Bestehende Datensätze behalten sie.',

    // ── System panel ──────────────────────────────────────────────────────
    system_heading: 'System',
    label_domain: 'Domain',
    label_region: 'Region',
    region_value: 'OVH Public Cloud · 3 AZ + Anbieter-Backups',
    label_fiscal: 'Maßgebliches Steuersystem',
    fiscal_value: 'Holded (Veri*factu / SII)',
    label_retention: 'Steuerliche Aufbewahrung',
    retention_value: '4–6 Jahre (hat Vorrang vor der Löschung)',
    label_erasure: 'Recht auf Löschung',
    erasure_value: 'Nicht-steuerliche personenbezogene Daten anonymisieren',
    label_backups: 'Backups',
    backups_value: 'Nächtliches pg_dump zusätzlich zu den Anbieter-Backups',
  },
};

export default dict;
